import React from 'react';
import { Box, Container, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import SettingsSuggestOutlinedIcon from '@mui/icons-material/SettingsSuggestOutlined';
import FlashOnOutlinedIcon from '@mui/icons-material/FlashOnOutlined';

const steps = [
  {
    icon: <MapOutlinedIcon sx={{ fontSize: 20 }} />,
    number: '01',
    title: 'Survey',
    description: 'Map out your site and plan sensor placement before you arrive on-site.',
  },
  {
    icon: <SettingsSuggestOutlinedIcon sx={{ fontSize: 20 }} />,
    number: '02',
    title: 'Provision',
    description: 'Scan sensor IDs and configure settings directly from your phone.',
  },
  {
    icon: <FlashOnOutlinedIcon sx={{ fontSize: 20 }} />,
    number: '03',
    title: 'Deploy',
    description: 'Mount, test, and confirm connectivity in minutes. Data flows immediately.',
  },
];

const DeviceManagerSection = () => {
  return (
    <Box
      sx={{
        position: 'relative',
        backgroundColor: '#030303',
        py: { xs: 12, md: 18 },
        overflow: 'hidden',
      }}
    >
      {/* Subtle radial glow */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '30%',
          transform: 'translate(-50%, -50%)',
          width: 700,
          height: 700,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(0,77,64,0.14) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={{ xs: 8, md: 12 }} alignItems="center">
          {/* Text side */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography
              component="span"
              sx={{
                fontFamily: 'Outfit',
                fontWeight: 500,
                fontSize: '0.78rem',
                color: '#004D40',
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                display: 'block',
                mb: 1.5,
              }}
            >
              Mobile App
            </Typography>
            <Typography
              component="h2"
              sx={{
                fontFamily: 'Outfit',
                fontWeight: 800,
                fontSize: { xs: '2.2rem', md: '3rem' },
                color: '#ffffff',
                lineHeight: 1.08,
                letterSpacing: '-0.03em',
                mb: 3,
              }}
            >
              Seamless deployment with our Device Manager App
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Outfit',
                fontWeight: 300,
                fontSize: '1rem',
                color: 'rgba(255,255,255,0.5)',
                lineHeight: 1.85,
                mb: 8,
                maxWidth: 460,
              }}
            >
              Install sensors quickly and efficiently. Survey sites, provision, test and deploy
              in minutes with our simple yet powerful technology.
            </Typography>

            {/* Numbered steps */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {steps.map((step, i) => (
                <Box
                  key={step.number}
                  sx={{
                    display: 'flex',
                    gap: 3,
                    py: 3.5,
                    borderTop: '1px solid rgba(255,255,255,0.07)',
                    ...(i === steps.length - 1 && { borderBottom: '1px solid rgba(255,255,255,0.07)' }),
                    alignItems: 'flex-start',
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Outfit',
                      fontWeight: 700,
                      fontSize: '0.72rem',
                      color: '#004D40',
                      letterSpacing: '0.1em',
                      mt: 0.2,
                      minWidth: 28,
                    }}
                  >
                    {step.number}
                  </Typography>
                  <Box>
                    <Typography
                      sx={{
                        fontFamily: 'Outfit',
                        fontWeight: 700,
                        fontSize: '1rem',
                        color: '#ffffff',
                        mb: 0.5,
                        letterSpacing: '-0.01em',
                      }}
                    >
                      {step.title}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: 'Outfit',
                        fontWeight: 300,
                        fontSize: '0.9rem',
                        color: 'rgba(255,255,255,0.45)',
                        lineHeight: 1.75,
                      }}
                    >
                      {step.description}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Grid>

          {/* Phone mockup */}
          <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box
              sx={{
                position: 'relative',
                width: { xs: 220, md: 260 },
              }}
            >
              {/* Outer glow ring */}
              <Box
                sx={{
                  position: 'absolute',
                  inset: -20,
                  borderRadius: '50px',
                  background: 'radial-gradient(ellipse at center, rgba(0,77,64,0.25) 0%, transparent 70%)',
                  pointerEvents: 'none',
                }}
              />
              <Box
                component="img"
                src="/phone.png"
                alt="Device Manager App"
                sx={{
                  width: '100%',
                  display: 'block',
                  position: 'relative',
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default DeviceManagerSection;